<div class="settings">
  <div class="settings-header">
    <div class="title">Settings</div>
    <div class="close" (click)="closeSettings()"></div>
  </div>

  <div class="settings-body">
    <!-- Who is signed in — the header collapses the name to an icon on
         handheld widths, so this is the only place a scanner user can see it. -->
    <div class="settings-section user-section">
      <div class="section-label">Signed In</div>
      <div class="user-name">
        <ng-container *ngIf="user">{{user.firstName}} {{user.lastName}}</ng-container>
        <ng-container *ngIf="!user">&mdash;</ng-container>
      </div>
      <div *ngIf="user && (user.email || deviceSession)" class="user-detail">
        <span *ngIf="user.email">{{user.email}}</span>
        <span *ngIf="user.email && deviceSession"> &middot; </span>
        <span *ngIf="deviceSession">PIN session</span>
      </div>
    </div>

    <div class="settings-section device-section">
      <div class="section-label">This Device</div>
      <div class="device-setting">
        <div class="device-setting-name">Show Numberpad</div>
        <div class="segmented">
          <div *ngFor="let option of numberpadOptions"
               class="segment"
               [class.active]="showNumberpadOverride === option.key"
               (click)="setShowNumberpad(option.key)">
            {{option.label}}
          </div>
        </div>
        <div class="device-setting-hint">
          Devices with a built-in keypad can turn the on-screen numberpad off to
          show item details instead. Client default: {{clientNumberpadDefaultLabel()}}.
        </div>
      </div>

      <!-- PIN sign-in authorization for this device -->
      <div *ngIf="deviceAuth || canAuthorizeDevice" class="device-setting device-auth">
        <div class="device-setting-name">PIN Sign-In</div>

        <ng-container *ngIf="deviceAuth">
          <div class="device-auth-status">
            <div class="status-row">
              <span class="status-badge">Authorized</span>
              <span class="status-name">{{deviceAuth.name}}</span>
            </div>
            <div class="status-detail">{{deviceAuth.locationName}}</div>
            <div class="status-detail">
              Expires {{deviceAuth.expiresAt | date:'MMM d, y'}}
              ({{deviceDaysRemaining()}} {{deviceDaysRemaining() === 1 ? 'day' : 'days'}} left)
              <ng-container *ngIf="deviceAuth.authorizedBy"> &middot; by {{deviceAuth.authorizedBy}}</ng-container>
            </div>
          </div>
          <div class="update-button danger" [class.busy]="removingDevice" (click)="removeDeviceAuthorization()">
            {{removingDevice ? 'Removing…' : 'Remove Authorization'}}
          </div>
          <div class="device-setting-hint">
            Users at {{deviceAuth.locationName}} can unlock this device with their PIN.
            Removing the authorization brings back the password sign-in.
          </div>
        </ng-container>

        <ng-container *ngIf="!deviceAuth && canAuthorizeDevice">
          <div *ngIf="!authorizeOpen" class="update-button" (click)="openAuthorize()">Authorize for PIN Sign-In</div>
          <div *ngIf="!authorizeOpen" class="device-setting-hint">
            Let users at a location sign in on this device with their PIN for up to
            {{deviceAuthMaxDays}} {{deviceAuthMaxDays === 1 ? 'day' : 'days'}}.
          </div>

          <div *ngIf="authorizeOpen" class="device-auth-form">
            <label>Device Name</label>
            <input type="text" [(ngModel)]="authorizeName" placeholder="e.g. Warehouse Scanner 3" maxlength="60" />

            <label>Location</label>
            <select [(ngModel)]="authorizeLocationId">
              <option *ngFor="let location of authorizeLocations" [value]="location._id">{{location.name}}</option>
            </select>

            <label>Days</label>
            <input type="number" [(ngModel)]="authorizeDays" min="1" [max]="deviceAuthMaxDays" step="1" />
            <div class="device-setting-hint">
              Up to {{deviceAuthMaxDays}} {{deviceAuthMaxDays === 1 ? 'day' : 'days'}}. Your session ends
              once the device is authorized, leaving it on the PIN screen.
            </div>

            <div class="device-auth-actions">
              <div class="update-button secondary" (click)="cancelAuthorize()">Cancel</div>
              <div class="update-button ready" [class.busy]="authorizing" (click)="authorizeDevice()">
                {{authorizing ? 'Authorizing…' : 'Authorize'}}
              </div>
            </div>
          </div>
        </ng-container>

        <div *ngIf="deviceError" class="update-status error">{{deviceError}}</div>
      </div>

      <!-- Local Inventory Cache: this device keeps the catalog and queues transfer changes -->
      <div class="device-setting device-cache">
        <div class="device-setting-name">Local Inventory Cache</div>
        <div class="segmented">
          <div class="segment" [class.active]="localCacheEnabled" (click)="setLocalCache(true)">On</div>
          <div class="segment" [class.active]="!localCacheEnabled" (click)="setLocalCache(false)">Off</div>
        </div>
        <div class="device-setting-hint">
          Keeps the inventory list on this device so scans on transfers resolve instantly and keep
          working through wifi drops. Changes made offline are sent when the connection returns.
        </div>
        <div *ngIf="localCacheEnabled && cacheStatus" class="device-cache-status">
          <div class="status-row">
            <span class="status-badge" [class.warn]="cacheStatus.state === 'error' || cacheStatus.state === 'empty'">
              {{cacheStateLabel()}}
            </span>
            <span class="status-name">{{cacheStatus.itemCount | rf:'number'}} items</span>
          </div>
          <div *ngIf="cacheStatus.lastSync" class="status-detail">Last synced {{cacheStatus.lastSync | rf:'dateTime'}}</div>
          <div *ngIf="cacheStatus.progress" class="status-detail">Loading… page {{cacheStatus.progress.pages}} ({{cacheStatus.progress.items | rf:'number'}} items so far)</div>
          <div *ngIf="cacheStatus.error" class="update-status error">{{cacheStatus.error}}</div>
          <div *ngIf="queueStatus && (queueStatus.pending > 0 || queueStatus.failed > 0)" class="status-detail">
            {{queueStatus.pending}} change{{queueStatus.pending === 1 ? '' : 's'}} waiting to send<ng-container *ngIf="queueStatus.failed > 0"> · {{queueStatus.failed}} failed</ng-container>
          </div>
          <div class="device-auth-actions">
            <div class="update-button secondary" (click)="syncCacheNow()">Sync Now</div>
            <div class="update-button secondary" (click)="reloadCache()">Reload Everything</div>
          </div>
        </div>
      </div>
    </div>

    <div class="settings-section app-section">
      <div class="section-label">App</div>
      <div class="app-version-row">
        <div class="app-version-label">Version</div>
        <div class="app-version-value">v{{version}}</div>
      </div>

      <div *ngIf="!updateReady" class="update-button" [class.busy]="updateStatus === 'checking'" (click)="checkForUpdates()">
        <span *ngIf="updateStatus !== 'checking'">Check for Updates</span>
        <span *ngIf="updateStatus === 'checking'">Checking&hellip;</span>
      </div>
      <div *ngIf="updateReady" class="update-button ready" (click)="restartToUpdate()">Restart to Update</div>

      <div *ngIf="updateStatus === 'up-to-date'" class="update-status">You're on the latest version.</div>
      <div *ngIf="updateReady" class="update-status">A new version has been downloaded. Restart to apply it.</div>
      <div *ngIf="updateStatus === 'error'" class="update-status error">Unable to check for updates. Check the connection and try again.</div>
    </div>

    <div class="settings-section">
      <div class="section-label">Theme</div>
      <div class="theme-options">
        <div *ngFor="let theme of themes"
             class="theme-option"
             [class.active]="activeTheme === theme.key"
             (click)="setTheme(theme.key)">
          <div class="theme-swatch" [attr.data-theme]="theme.key"></div>
          <div class="theme-name">{{theme.label}}</div>
          <svg *ngIf="activeTheme === theme.key" class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
      </div>
    </div>
  </div>
</div>
